import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import apiRequest from '../../api/request';

const initialState = {
  tracker: {
    responseData: {},
    loading: false,
    error: null,
  },
  // The Accounts Manager (department income) for the project departments, shown beside the CRO grid
  departmentIncome: {
    responseData: {},
    loading: false,
    error: null,
  },
};

// CRO Project Tracker: per-CRO / per-project sales + fee revenue vs target. Read-only.
// Rules live in node-portal-from-29-08-2022 src/util/functions/cro_project_tracker.query.js
export const getCroProjectTracker = createAsyncThunk('croProjectTracker/getCroProjectTracker', async (dispatchParams) => {
  const params = { month: dispatchParams.month };
  if (dispatchParams.skillx) params.skillx = 'true';
  // Advanced: count the CRA-ECPC wing as its own STC column (off = ECPC stays inside Little Genie)
  if (dispatchParams.ecpcWing) params.ecpcWing = 'true';
  if (dispatchParams.employeeId) params.id = String(dispatchParams.employeeId);
  // a week window (Week 1-4 like the sheet); targets still come from the month it starts in
  if (dispatchParams.fromDate && dispatchParams.toDate) {
    params.fromDate = dispatchParams.fromDate;
    params.toDate = dispatchParams.toDate;
  }
  if (dispatchParams.hardRefresh) params.hardRefresh = 'true';
  const urlParams = new URLSearchParams(params);
  const res = await apiRequest({ method: 'get', url: `/cro-project-tracker?${urlParams}` });
  return res;
});

// Accounts Manager (Department Income Tracker) for the departments that have a project mapped, narrowed on the server to what
// the viewer may see (their own department's team: that department only; Finance / management: all). Same response shape as
// /department-income-tracker/daily-collection ({ meta, data }), so the Accounts Manager components render it as they are.
export const getProjectDepartmentIncome = createAsyncThunk('croProjectTracker/getProjectDepartmentIncome', async (dispatchParams) => {
  const params = { month: dispatchParams.month };
  if (dispatchParams.skillx) params.skillx = 'true';
  if (dispatchParams.hardRefresh) params.hardRefresh = 'true';
  const urlParams = new URLSearchParams(params);
  const res = await apiRequest({ method: 'get', url: `/cro-project-tracker/department-income?${urlParams}` });
  return res;
});

const croProjectTrackerSlice = createSlice({
  name: 'croProjectTracker',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder.addCase(getCroProjectTracker.pending, (state) => {
      state.tracker.loading = true;
      state.tracker.error = null;
    });
    builder.addCase(getCroProjectTracker.fulfilled, (state, action) => {
      state.tracker.loading = false;
      // response() hoists data to the envelope top level (see departmentIncomeTrackerSlice),
      // so the envelope already carries `.data` - store it whole.
      state.tracker.responseData = action.payload || {};
      // an error envelope must not look like "no data" - surface it
      state.tracker.error = action.payload?.status === 'error' ? action.payload.message || 'Request failed' : null;
    });
    builder.addCase(getCroProjectTracker.rejected, (state, action) => {
      state.tracker.loading = false;
      state.tracker.error = action.error?.message || 'Could not load the Project Tracker';
    });

    builder.addCase(getProjectDepartmentIncome.pending, (state) => {
      state.departmentIncome.loading = true;
      state.departmentIncome.error = null;
    });
    builder.addCase(getProjectDepartmentIncome.fulfilled, (state, action) => {
      state.departmentIncome.loading = false;
      // response() hoists data.meta / data.data to the envelope top level - store it whole (like the Accounts Manager's slice)
      state.departmentIncome.responseData = action.payload || {};
      state.departmentIncome.error = action.payload?.status === 'error' ? action.payload.message || 'Request failed' : null;
    });
    builder.addCase(getProjectDepartmentIncome.rejected, (state, action) => {
      state.departmentIncome.loading = false;
      state.departmentIncome.error = action.error?.message || 'Could not load the department income';
    });
  },
});

export default croProjectTrackerSlice.reducer;
